import {z} from '@hono/zod-openapi';

import {VoiceDebugSpendProjectionSchema} from './VoiceDebugSpendProjectionSchema';
import {VoiceDebugTerminalOutcomeSchema} from './VoiceDebugTerminalOutcomeSchema';

const MAX_EVENT_TEXT_LENGTH = 16_384;

export const VoiceDebugEventSchema = z
  .object({
    schemaVersion: z.literal('1.0.0'),
    sequence: z.number().int().nonnegative().max(1_000_000),
    at: z.string().datetime({offset: true}),
    sessionId: z.string().min(1).max(128),
    voiceConversationId: z.string().min(1).max(128),
    payload: z.discriminatedUnion('type', [
      z
        .object({
          type: z.literal('connection'),
          state: z.enum(['connecting', 'active', 'disconnected']),
        })
        .strict(),
      z
        .object({
          type: z.literal('scenario'),
          phase: z.string().min(1).max(128),
          currentQuestion: z.string().max(4096).nullable(),
        })
        .strict(),
      z
        .object({
          type: z.literal('transcript'),
          speaker: z.enum(['agent', 'caller']),
          status: z.enum(['partial', 'final']),
          text: z.string().max(MAX_EVENT_TEXT_LENGTH),
          // Caller fragments use callerTurnId; agent fragments use responseId.
          turnId: z.string().min(1).max(128).openapi({example: 'resp_67890'}),
        })
        .strict(),
      z
        .object({
          type: z.literal('capability'),
          requestId: z.string().min(1).max(128),
          name: z.string().min(1).max(128),
          argumentsJson: z.string().max(MAX_EVENT_TEXT_LENGTH).nullable(),
          status: z.enum([
            'requested',
            'success',
            'not-found',
            'ambiguous',
            'timeout',
            'unauthorized',
            'technical-failure',
          ]),
        })
        .strict(),
      z.object({type: z.literal('spend'), spend: VoiceDebugSpendProjectionSchema}).strict(),
      z
        .object({
          type: z.literal('terminal'),
          outcome: VoiceDebugTerminalOutcomeSchema,
        })
        .strict(),
      // Observation failures are recoverable; they do not end the conversation.
      z
        .object({
          type: z.literal('notice'),
          message: z.string().min(1).max(MAX_EVENT_TEXT_LENGTH),
        })
        .strict(),
    ]),
  })
  .strict()
  .openapi('VoiceDebugEventSchema');

export type VoiceDebugEvent = z.infer<typeof VoiceDebugEventSchema>;
